import React, { useEffect, useMemo, useState } from 'react';
import { auth, db } from '../../firebase';
import { useAuth } from '../../hooks/useAuth';
import { apiGetReferralParticipants, apiResendParticipantInvite } from '../utils/rmsApi';
import { doc, collection, onSnapshot, query, where, getDocs } from 'firebase/firestore';
import { UserIcon } from '@heroicons/react/outline';
import {
  STATUS,
  calculateRoleStatus,
  getStatusBadgeConfig,
  buildFormProgressFromRecord,
} from '../utils/referralStatus';
import { PARTICIPANT_STATUS, PENDING_PARTICIPANT_STATUSES } from '../../constants/participantStatuses';

/**
 * Mapping of school role optionIds to human-readable labels
 * From request_for_referral.py - "What is your job role at the education setting?"
 */
const SCHOOL_ROLE_LABELS = {
  class_teacher: 'Class teacher',
  senco: 'SENCO',
  headteacher: 'Headteacher',
  lsa: 'Learning Support Assistant',
  educational_psychologist: 'Educational Psychologist',
};

/**
 * Mapping of guardian relationship optionIds to human-readable labels
 * From request_for_referral.py - "What is your relationship to the child or young person?"
 */
const GUARDIAN_RELATIONSHIP_LABELS = {
  parent: 'Parent',
  step_parent: 'Step-parent',
  foster_carer: 'Foster carer',
  kinship_carer: 'Kinship carer',
  legal_guardian: 'Legal guardian',
  informal_carer: 'Informal carer',
};

/**
 * Formats a relationship optionId to its display label.
 * Handles "other" by using the free text value (trimmed + first letter capitalized).
 * 
 * @param {string} optionId - The relationship optionId (e.g., "parent", "class_teacher", "other")
 * @param {string} otherText - The free text value if optionId is "other"
 * @param {Object} labelMap - The mapping object (SCHOOL_ROLE_LABELS or GUARDIAN_RELATIONSHIP_LABELS)
 * @param {string} fallback - Default label if no match found
 * @returns {string} The formatted display label
 */
const formatRelationshipLabel = (optionId, otherText, labelMap, fallback) => {
  if (!optionId) return fallback;
  
  // Handle "other" with free text
  if (optionId === 'other' && otherText) {
    const trimmed = otherText.trim();
    if (trimmed) {
      // Capitalize first letter, keep rest as-is
      return trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
    }
    return fallback;
  }
  
  // Look up label from mapping
  return labelMap[optionId] || fallback;
};

/**
 * PeoplePanel
 * -----------
 * Shows "People involved in this referral" with their roles and form completion statuses.
 * - Displays school representative (from patient record creator)
 * - Displays guardians/parents from participants collection
 * - Shows status badges: Not Assigned, Not Started, Started, Completed
 * - Allows resending invites for guardians in pending states
 */
export default function PeoplePanel({ patientRecordId, viewerRole }) {
  const { claims } = useAuth();
  const orgId = claims?.rmsOrgId;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [schoolUserProfile, setSchoolUserProfile] = useState(null);
  // The record creator's uid, streamed from the patient record; keys the
  // schoolUsers profile lookup below.
  const [createdByUserId, setCreatedByUserId] = useState(null);
  const [formProgress, setFormProgress] = useState({});
  const [triageAssigned, setTriageAssigned] = useState(false);
  const [patientCreatedAt, setPatientCreatedAt] = useState(null);
  // 'primary' | 'secondary' | null. Secondary referrals skip the HFP, so status
  // must not require it (TD-137).
  const [schoolPhase, setSchoolPhase] = useState(null);
  // 'asd' | 'adhd' | 'both' | null. Autism-only referrals skip the ADHD SNAP-IV
  // scale, so status must not require it (TD-139).
  const [intakeConcern, setIntakeConcern] = useState(null);
  // The record itself selects the HFP set (v3 for Harbour pupils).
  const [patientRecord, setPatientRecord] = useState(null);
  const user = auth.currentUser;

  // Fetch participants from API
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        if (!orgId || !patientRecordId) return;
        const { participants: list } = await apiGetReferralParticipants({ orgId, patientRecordId });
        if (!cancelled) setParticipants(Array.isArray(list) ? list : []);
      } catch (e) {
        if (!cancelled) setError(e?.message || 'Failed to load participants');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [orgId, patientRecordId]);

  // Live patient record: creation date, school phase, intake concern, the
  // record itself (Harbour pupils select the v3 HFP set) — and the real-time
  // form progress for status calculation, folded from the record's
  // formProgress mirror (TDI-154; client formInstances reads are
  // provider-only). The shared fold keeps the completionPercent invariant:
  // submitted forms count as 100 even when their mirrored percent is
  // missing/stale, and any triage instance means triage is assigned —
  // aligned with the service-user dashboard. A record without the mirror
  // (pre-backfill) folds to an empty map: every form reads as not started.
  useEffect(() => {
    if (!orgId || !patientRecordId) return undefined;
    const prRef = doc(db, 'rms_organisations', orgId, 'patientRecords', patientRecordId);
    const unsub = onSnapshot(prRef, (snap) => {
      try {
        const data = snap.exists() ? (snap.data() || {}) : {};
        // Get patient creation date
        setPatientCreatedAt(data.createdAt || null);
        // Capture the school phase so status calc can exclude HFP for secondary.
        setSchoolPhase(data.schoolPhase || data.school?.schoolType || null);
        // Capture intake concern so status calc can exclude the SNAP-IV for asd-only.
        setIntakeConcern(data.intakeConcern || null);
        setPatientRecord(data);
        setCreatedByUserId(data.createdByUserId || null);
        const { formProgress: progress, triageAssigned: triageFound } = buildFormProgressFromRecord(data);
        setFormProgress(progress);
        setTriageAssigned(triageFound);
      } catch (_e) {
        setFormProgress({});
        setTriageAssigned(false);
      }
    }, (_err) => {
      setFormProgress({});
      setTriageAssigned(false);
    });
    return () => unsub();
  }, [orgId, patientRecordId]);

  // Load the school representative's profile (schoolUsers by accountId), keyed
  // on the creator uid streamed from the record above.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (!orgId || !createdByUserId) return;
        const suQ = query(collection(db, 'rms_organisations', orgId, 'schoolUsers'), where('accountId', '==', createdByUserId));
        const suSnap = await getDocs(suQ);
        if (cancelled) return;
        if (!suSnap.empty) {
          const d = suSnap.docs[0].data() || {};
          setSchoolUserProfile({
            firstName: d.firstName || '',
            lastName: d.lastName || '',
            email: d.email || '',
            createdAt: d.createdAt || null,
          });
        } else {
          setSchoolUserProfile(null);
        }
      } catch (_) {
        if (!cancelled) setSchoolUserProfile(null);
      }
    })();
    return () => { cancelled = true; };
  }, [orgId, createdByUserId]);

  // Filter guardians: exclude 'superseded' participants to avoid showing old nominations
  // when the school user changed the guardian's email before the invite was consumed
  const guardians = useMemo(() => (participants || []).filter(p => p.role === 'guardian' && p.status !== 'superseded'), [participants]);
  const hasGuardian = guardians.length > 0;

  // The school-rep participant carries the initiator's name/email directly (the
  // same shape guardians use). It's the fallback for the card when the
  // schoolUsers profile lookup (by createdByUserId) finds nothing — e.g. the
  // record predates account linking — so the card shows the person's name
  // rather than only their role label.
  const schoolParticipant = useMemo(
    () => (participants || []).find(p => p.role === 'school_user') || null,
    [participants]
  );
  const schoolUserName = useMemo(() => {
    const profileName = schoolUserProfile
      ? `${schoolUserProfile.firstName || ''} ${schoolUserProfile.lastName || ''}`.trim()
      : '';
    if (profileName) return profileName;
    const participantName = schoolParticipant
      ? `${schoolParticipant.firstName || ''} ${schoolParticipant.lastName || ''}`.trim()
      : '';
    return participantName || null;
  }, [schoolUserProfile, schoolParticipant]);

  /**
   * Calculate status for a role.
   *
   * IMPORTANT:
   * We use the shared `calculateRoleStatus` helper so the People popup and the
   * service-user dashboard table cannot drift on form-key changes again.
   */
  const getRoleStatus = (role) => {
    return calculateRoleStatus({
      role,
      formProgress,
      triageAssigned,
      // Guardian-only fields (ignored for school users)
      hasGuardian,
      guardianInviteStatus: null,
      schoolPhase,
      intakeConcern,
      patient: patientRecord,
    });
  };

  const canResend = (p) => {
    return p?.role === 'guardian' && PENDING_PARTICIPANT_STATUSES.includes(p?.status);
  };

  const onResend = async (p) => {
    try {
      await apiResendParticipantInvite({ orgId, patientRecordId, participantId: p.id });
      setParticipants(prev => prev.map(x => x.id === p.id ? { ...x, status: PARTICIPANT_STATUS.INVITE_SENT } : x));
    } catch (_) {}
  };

  const schoolStatus = getRoleStatus('school_user');
  
  // Get the form-based guardian status (used when account is linked)
  const guardianFormStatus = getRoleStatus('guardian');
  
  // Format the school user's role label from the participant's roleTitle
  // (returned by rmsGetReferralParticipants — we no longer read their
  // requestForReferral instance, whose client reads are provider-only,
  // TDI-154). The stored value may be an optionId (mapped via
  // SCHOOL_ROLE_LABELS), an { optionId, otherText } object, or free text from
  // a guardian's nomination. Falls back to "School Representative" when no
  // role data is available.
  const schoolRoleLabel = useMemo(() => {
    const raw = schoolParticipant?.roleTitle;
    if (!raw) return 'School Representative';
    if (typeof raw === 'object') {
      return formatRelationshipLabel(
        raw.optionId,
        raw.otherText,
        SCHOOL_ROLE_LABELS,
        'School Representative'
      );
    }
    if (SCHOOL_ROLE_LABELS[raw]) return SCHOOL_ROLE_LABELS[raw];
    const trimmed = String(raw).trim();
    if (!trimmed) return 'School Representative';
    return trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
  }, [schoolParticipant]);
  
  /**
   * Format guardian relationship label from participant data
   * The relationship is stored as optionId string in the participant document
   * If "other", there might be an otherText field (though less common for guardians)
   */
  const formatGuardianRelationship = (guardian) => {
    if (!guardian?.relationship) return 'Parent / Carer';
    
    // relationship might be stored as string or object
    const optionId = typeof guardian.relationship === 'object' 
      ? guardian.relationship.optionId 
      : guardian.relationship;
    const otherText = typeof guardian.relationship === 'object' 
      ? guardian.relationship.otherText 
      : guardian.relationshipOther;
    
    return formatRelationshipLabel(
      optionId,
      otherText,
      GUARDIAN_RELATIONSHIP_LABELS,
      'Parent / Carer'
    );
  };

  // Determine if guardian has set up their account (for date label)
  const isGuardianAccountLinked = (g) => {
    return g.status === PARTICIPANT_STATUS.ACCOUNT_LINKED;
  };
  
  /**
   * Get the display status for a specific guardian participant
   * Takes into account their invite/account status before considering form progress
   * 
   * Flow: nominated/invite_sent → INVITE_SENT
   *       account_linked → form-based status (NOT_STARTED/STARTED/COMPLETED)
   */
  const getGuardianDisplayStatus = (g) => {
    // If invite is pending or sent but not consumed, show "Invite Sent"
    if (PENDING_PARTICIPANT_STATUSES.includes(g.status)) {
      return STATUS.INVITE_SENT;
    }
    // If account is linked, use form-based status
    if (isGuardianAccountLinked(g)) {
      return guardianFormStatus;
    }
    // Fallback (shouldn't happen in normal flow)
    return STATUS.NOT_STARTED;
  };

  return (
    <div className="space-y-3">
      {/* School Representative Card */}
      <PersonCard
        role={schoolRoleLabel}
        isCurrentUser={viewerRole === 'school_user'}
        status={schoolStatus}
        name={schoolUserName}
        email={schoolUserProfile?.email || schoolParticipant?.email || user?.email}
        placeholder="No school representative has been nominated yet"
        joinedAt={schoolUserProfile?.createdAt || patientCreatedAt}
        joinedLabel="Account created on"
      />

      {/* Parent / Carer Card(s) */}
      {loading && (
        <div className="p-4 bg-gray-50 rounded-xl text-sm text-gray-600">Loading...</div>
      )}
      
      {!loading && error && (
        <div className="p-4 bg-red-50 rounded-xl text-sm text-red-600">{error}</div>
      )}
      
      {!loading && !error && guardians.length === 0 && (
        <PersonCard
          role="Parent / Carer"
          isCurrentUser={viewerRole === 'guardian'}
          status={STATUS.NOT_ASSIGNED}
          name={null}
          email={null}
          placeholder="No parent / carer has been nominated yet"
        />
      )}
      
      {!loading && !error && guardians.map((g) => (
        <PersonCard
          key={g.id}
          role={formatGuardianRelationship(g)}
          isCurrentUser={viewerRole === 'guardian'}
          status={getGuardianDisplayStatus(g)}
          name={`${g.firstName || ''} ${g.lastName || ''}`.trim()}
          email={g.email}
          joinedAt={isGuardianAccountLinked(g) ? g.updatedAt : g.createdAt}
          joinedLabel={isGuardianAccountLinked(g) ? 'Account created on' : 'Invited on'}
          canResend={canResend(g)}
          onResend={() => onResend(g)}
        />
      ))}
    </div>
  );
}

/**
 * Status badge component - displays form completion status with appropriate styling
 */
function StatusBadge({ status }) {
  const { label, className } = getStatusBadgeConfig(status);
  
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-xs font-medium ${className}`}>
      {label}
    </span>
  );
}

/**
 * Person card component - displays a participant with their info and status
 */
function PersonCard({ 
  role, 
  isCurrentUser, 
  status, 
  name, 
  email, 
  joinedAt, 
  joinedLabel,
  placeholder,
  canResend,
  onResend 
}) {
  const formattedDate = useMemo(() => {
    if (!joinedAt) return null;
    try {
      let date = null;
      if (typeof joinedAt === 'object' && typeof joinedAt.seconds === 'number') {
        date = new Date(joinedAt.seconds * 1000);
      } else if (joinedAt instanceof Date) {
        date = joinedAt;
      } else {
        date = new Date(joinedAt);
      }
      if (!date || isNaN(date.getTime())) return null;
      
      const day = date.getDate();
      const daySuffix = (d) => {
        if (d >= 11 && d <= 13) return 'th';
        switch (d % 10) {
          case 1: return 'st';
          case 2: return 'nd';
          case 3: return 'rd';
          default: return 'th';
        }
      };
      const monthNames = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
      return `${day}${daySuffix(day)} ${monthNames[date.getMonth()]} ${date.getFullYear()}`;
    } catch (_) {
      return null;
    }
  }, [joinedAt]);

  const isNotAssigned = status === STATUS.NOT_ASSIGNED;

  return (
    <div className={`p-4 rounded-xl border ${isNotAssigned ? 'bg-gray-50 border-gray-200' : 'bg-white border-gray-200'}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          {/* User avatar */}
          <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
            isNotAssigned ? 'bg-gray-200' : 'bg-primary/10'
          }`}>
            <UserIcon className={`w-5 h-5 ${isNotAssigned ? 'text-gray-400' : 'text-primary'}`} />
          </div>
          
          <div className="min-w-0">
            {/* Name and details - show name as primary if available, otherwise role */}
            {isNotAssigned && placeholder ? (
              <>
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-sm font-semibold text-gray-800">{role}</span>
                </div>
                <p className="text-sm text-gray-500 italic">{placeholder}</p>
              </>
            ) : (
              <>
                {/* Primary heading: name if available, otherwise role */}
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-gray-900">
                    {name || role}
                  </span>
                  {isCurrentUser && (
                    <span className="text-xs text-primary font-medium">(You)</span>
                  )}
                </div>
                
                {/* Show role as subtitle if name is displayed */}
                {name && (
                  <p className="text-xs text-gray-500">{role}</p>
                )}
                
                {email && (
                  <p className="text-xs text-gray-600 truncate mt-0.5">{email}</p>
                )}                
                {formattedDate && joinedLabel && (
                  <p className="text-xs text-gray-500 mt-1">
                    {joinedLabel}: {formattedDate}
                  </p>
                )}
              </>
            )}
          </div>
        </div>
        
        {/* Status badge */}
        <div className="flex flex-col items-end gap-2">
          <StatusBadge status={status} />
          {canResend && onResend && (
            <button
              type="button"
              className="text-xs text-primary hover:underline"
              onClick={onResend}
            >
              Resend invite
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
